import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GeoMap } from "@cisco/geomaps";
import { PropTypes } from "prop-types";
import _ from "lodash";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";

//i18n files
import { useI18n } from "../../i18n/useI18n";

//utils
import {
  formatNumberNDecimalPlaces,
  formatBytes,
  formatString
} from "../../utils/format";
import { getColor, colors } from "../../utils/colors";
import {
  isRecommendationPage,
  isApplyRevertRecommendation
} from "../../utils/waniCommon";
import {
  validateSitesLocation,
  setLocalStorageFlag,
  getLocalStorageFlag,
  randomUUID,
  updateCookie,
} from "../../utils/common";
import { getIcon } from "../../utils/icons";
import { routeURLs } from "../../utils/enums";
import { BASE_URL } from "../../apis/apiConstants";
import { getDonutChartCluster } from "./renderCluster";
import { getCurrentTheme, createThemeChangeObserver, applyMapDarkTheme } from "../Theme";

//style
import css from "../commonMagneticStyle.less";

//components
import AppHOC from "../../generics/AppHOC";
import NoDataAvailable from "../NoDataAvailable";

//reducers
import * as actions from "../../views/applications/actions";
import { getCookie } from "../../serviceWorker";
import { getVManageMonitorUrl } from "../../utils/vManageNavigation";
import { useMount } from "../../utils/genericCommon";
import { renderReactNode } from "../../utils/reactRoot";

const mapStateToProps = state => {
  return {
    applications: state.vanalytics.applications
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

const GeoMapComponent = props => {
  const { tCommon } = useI18n('common');
  const { sites, options, sitesView, isAvailabilityScore } = props;
  const { appGroup, selectedSite, currentTimeStamp } = props.globalFilter;
  const appClassLabels = JSON.parse(getLocalStorageFlag("activeAppClasses")) || {};
  const {
    customMarker,
    disableClustering,
    showNavigation: showNavigation,
    showLegend: showLegend,
    experienceColors: EXPERIENCE
  } = options;
  const { GOOD, FAIR, POOR, READY, NOCHANGE, NA } = EXPERIENCE;
  const mapRef = useRef(null);
  const mount = useMount();
  const navigate = useNavigate();
  const mapVisualRef = useRef(null);
  const isEntireMapShown = validateSitesLocation(sites);
  const [loading, setLoading] = useState(true);
  const [isDarkTheme, setIsDarkTheme] = useState(() => getCurrentTheme() === 'dark');
  const isRecommendationColor = sitesView === "recommendation";

  const POINT_STATE = {
    OK: "ok",
    WAITING: "waiting",
    DOWN: "down",
    READY: "ready",
    NOCHANGE: "notReady",
    NA: "unknown"
  };

  const getDefaultMarkerOptions = experience => {
    let color = colors.cyan40;

    switch (experience) {
      case GOOD:
        color = GOOD;
        break;
      case FAIR:
        color = FAIR;
        break;
      case POOR:
        color = POOR;
        break;
      case NOCHANGE:
        color = NOCHANGE;
        break;
      case READY:
        color = READY;
        break;
      case NA:
        color = NA;
        break;
    }

    return {
      color: color
    };
  };

  const getClusterOptions = clusterOption => {
    const defaultClusterOption = {
      ok: 0,
      waiting: 0,
      down: 0,
      ready: 0,
      notReady: 0,
      unknown: 0
    };
    switch (true) {
      case GOOD === clusterOption && !isRecommendationColor:
        return { ...defaultClusterOption, ok: 1 };
      case FAIR === clusterOption:
        return { ...defaultClusterOption, waiting: 1 };

      case POOR === clusterOption:
        return { ...defaultClusterOption, down: 1 };
      case NA === clusterOption && !isRecommendationColor:
        return { ...defaultClusterOption, unknown: 1 };

      case NOCHANGE === clusterOption:
        return { ...defaultClusterOption, notReady: 1 };

      case READY === clusterOption:
        return { ...defaultClusterOption, ready: 1 };

      default:
        return { ...defaultClusterOption, ok: 1 };
    }
  };

  const getExperienceColorOnAvailabilityScore = (availability, actionColor = GOOD) => {
    let expColor = "";

    const { GOOD, FAIR, POOR, NA } = EXPERIENCE;

    if (availability >= 95 && availability <= 100) {
      expColor = GOOD;
    } else if (availability >= 80 && availability < 95) {
      expColor = FAIR;
    } else if (availability >= 0 && availability < 80) {
      expColor = POOR;
    } else if (availability < 0 || availability === "-") {
      expColor = NA;
    } else {
      expColor = actionColor;
    }

    return expColor;
  };

  const getExperienceColorOnQoEScore = qoeStatus => {
    let expColor = "";
    const { GOOD, FAIR, POOR, UNKNOWN } = EXPERIENCE;

    if (qoeStatus ==='good') {
      expColor = GOOD;
    } else if (qoeStatus ==='fair') {
      expColor = FAIR;
    } else if (qoeStatus ==='poor') {
      expColor = POOR;
    } else if (qoeStatus ==='unknown') {
      expColor = UNKNOWN;
    } else {
      expColor = GOOD;
    }

    return expColor;
  };

  const getDefaultMarker = (snoToShow, experience) => {
    const palette = getDefaultMarkerOptions(experience);
    const el = document.createElement("div");

    let svgToRender = ``;

    if (customMarker === "CIRCLE") {
      svgToRender = `<svg width="20" height="20">
            <circle cx='10' cy='10' r='10' fill='${palette.color}' />
          </svg>`;
    } else {
      svgToRender = `<svg width="50" height="20">
            <rect x="15" y="0" width="45" height="18" style="fill:${palette.color};stroke-width:3;stroke:${palette.color}" />
            <circle cx="10" cy="10" r="10" stroke="${colors.gray100}" stroke-width="3" fill="${palette.color}" />
            <circle cx="10" cy="10" r="7" fill="${colors.gray100}" />
            <text x='25' y='15' font-size='12' fill="${colors.gray100}" stroke='${colors.gray100}' stroke-width='1'>#  ${snoToShow}</text>
          </svg>`;
    }

    el.innerHTML = svgToRender;
    return el;
  }

  const pointsToShowWithAvailability = () => {
    return sites && sites.map((site, index) => {
      const {
        siteId,
        city,
        site_name,
        latitude,
        longitude,
        availability,
        usage,
        metrics,
        actionColor,
        actionState
      } = site;
      const experience = getExperienceColorOnAvailabilityScore(availability, actionColor);
      const snoToShow = index + 1;
      const siteID = siteId ? siteId : site.site_id;

      let defaultPointingOptions = {
        id: `${randomUUID()}${siteID !== undefined ? siteID : ""}`, //Appending siteId with UUID to access it inside features properties
        siteId: siteID,
        city: city,
        location: [longitude, latitude],
        availability: availability,
        experience: experience,
        usage: usage,
        defaultMarkerOptions: getDefaultMarkerOptions(experience),
        getMarker: () => getDefaultMarker(snoToShow, experience),
        name: site_name,
        sitesView: sitesView,
        metrics: metrics,
        actionState: actionState
      };

      if (!disableClustering) {
        defaultPointingOptions = {
          ...defaultPointingOptions,
          clusterOptions: getClusterOptions(experience)
        };
      }
      return defaultPointingOptions;
    })
  };

  const pointsToShowWithQoe = () => {
    return sites && sites.map(
      ({ site_id, latitude, longitude, vqoe_score, vqoe_status, usage }, index) => {

        const experience = getExperienceColorOnQoEScore(vqoe_status);
        const snoToShow = index + 1;

        let defaultPointingOptions = {
          id: `${randomUUID()}${site_id}`,
          siteId: site_id,
          location: [longitude, latitude],
          qoeScore: vqoe_score,
          usage: usage,
          clusterOptions: getClusterOptions(experience),
          getMarker: () => getDefaultMarker(snoToShow, experience),
        };
        return defaultPointingOptions;
      })
  };

  const getClusterMarkerProps = () => {
    return {
      stateColorMap: {
        ok: GOOD,
        waiting: FAIR,
        down: POOR,
        ready: READY,
        notReady: NOCHANGE,
        unknown: NA
      }
    };
  };

  const getClusterProperties = () => {
    return {
      ok: ["+", ["get", POINT_STATE.OK]],
      waiting: ["+", ["get", POINT_STATE.WAITING]],
      down: ["+", ["get", POINT_STATE.DOWN]],
      ready: ["+", ["get", POINT_STATE.READY]],
      notReady: ["+", ["get", POINT_STATE.NOCHANGE]],
      unknown: ["+", ["get", POINT_STATE.NA]]
    };
  };

  const getMapSettings = () => {
    return {
      zoom: 1,
      center: [0, 0],
      preserveDrawingBuffer: true
    };
  };

  const getSiteDetails = (e, siteId) => {
      if (e && e.target) {
        const isUIconvergence = getCookie("cl-analytics");

        if (isUIconvergence) {
          // If convergance is enabled, we need to update the cookie with the siteId and redirect to the manage site overview page
          updateCookie("currentSite", siteId);
          window.location.href = getVManageMonitorUrl("/overview");
        }
        else {
          // If convergance is not enabled, we need to update the local storage with the siteId and redirect to the overview page of the site with the siteId selected
          setLocalStorageFlag("actionCardSite", siteId);
          props.setSelectedSite(siteId);
          navigate(routeURLs.overview);
        }
      }
  };

  const createClusterElements = (cluster, qoe, label) => {
    return (
      <div className={`details-${qoe}`}>
        <div className={`details-${qoe}-label hbr-type-body3`}>{label}</div>
        <div className={`details-${qoe}-value`}>
          <div className={`details-${qoe}-value-icon`}>
            &nbsp;
          </div>
          <div className={`details-${qoe}-value-score`}>
            {cluster}
          </div>
        </div>
      </div>
    );
  };

  const getBodyContent = (metrics, siteId) => {
    //i.e. metrics is {google-workspace: {defaultQuality: '99.91', recommendationQuality: '99.91', actionState: 'READY'},office365: {defaultQuality: '99.82', recommendationQuality: '99.82', actionState: 'READY'}
    return Object.keys(metrics).map((elem, key) => {
      if (!metrics[elem].actionState) return <></>;
      return (
        <tr key={key} id={`${elem},${siteId}`}>
          <td>
            <div>{getIcon(elem, "2.0em")}</div>
            <div>
              {appClassLabels[elem] || elem}
            </div>
          </td>
          <td>{metrics[elem].defaultQuality}</td>
          <td>{metrics[elem].recommendationQuality}</td>
          <td>
            <span className="tooltip-content-state-action-btn">
              {metrics[elem].actionState}
            </span>
          </td>
        </tr>
      );
    });
  };

  const getTooltipContent = (id, data) => {
    const el = document.createElement("div");
    el.className = "tooltip-content";
    // function to update tooltip arrow color based on theme
    applyMapDarkTheme(el);
    let toolTip = undefined;
    if (data?.props?.cluster) {
      const poorQoeWithinCluster = data.props.down;
      const fairQoeWithinCluster = data.props.waiting;
      const goodQoeWithinCluster = data.props.ok;
      const grayQoeWithinCluster = data.props.unknown;
      const ready = data.props.ready;
      const notReady = data.props.notReady;

      toolTip = (
        <div className="common-map-sites-tooltip-cluster">
          <div className="heading">
            {` ${tCommon('allSitesView.recoSites', { pointCount: data.props.point_count })}`}
          </div>
          {props.sitesView !== 'recommendation' ?
            <div className="details">
              {createClusterElements(
                goodQoeWithinCluster,
                "high",
                tCommon('allSiteHighLabel')
              )}
              {createClusterElements(
                fairQoeWithinCluster,
                "fair",
                tCommon('allSiteFairLabel')
              )}
              {createClusterElements(
                grayQoeWithinCluster,
                "unknown",
                tCommon('allSiteUnknownLabel')
              )}
              {createClusterElements(
                poorQoeWithinCluster,
                "poor",
                tCommon('allSiteLowLabel')
              )}
            </div> :
            <div className="details">
              {
                createClusterElements(
                  ready,
                  "ready",
                  tCommon('allSiteReadyLabel')
                )
              }
              {
                createClusterElements(
                  notReady,
                  "notReady",
                  tCommon('allSiteNotReadyLabel')
                )
              }
            </div>
          }
        </div>
      );
    } else if (data?.node && sitesView === "recommendation"
    ) {
      const { metrics, siteId, id, actionState } = data.node;
      let hasAppGroupActionState = false;
      // if actionState  isActive this means the site has deleted state for all app group
      // so we show no recommendation message in the tooltip and we ignore the metrics details
      const isActive = actionState && isApplyRevertRecommendation(actionState);

      if (!isActive) {
        // if metrics is empty we show no recommendation message in the tooltip
        if (metrics) {
          // we loop tru metric objec and check if at leat one app group has some state so we can show
          // action state in the tooltip if no app group has action then show no recommendation
          Object.keys(metrics).forEach(elem => {
            if ("actionState" in metrics[elem]) hasAppGroupActionState = true;
          });
        }
      }

      toolTip = (
        <table className="tooltip-content">
          <caption>
            <div>
              <span className="tooltip-content-site-id-tooltip">
                {tCommon('allSitesView.siteIDLabel')}
                {siteId ? siteId : id}
              </span>
            </div>
          </caption>
          {!_.isEmpty(metrics) && hasAppGroupActionState && (
            <thead>
              <tr>
                <th scope="col">
                  {tCommon('allSitesView.application')}
                </th>
                <th scope="col">
                  {tCommon('defaultPathQualtyMapLabel')}
                </th>
                <th scope="col">{tCommon('recoPathQualityMapLabel')}</th>
                <th scope="col">{tCommon('allSitesView.recoState')}</th>
              </tr>
            </thead>
          )}
          {!_.isEmpty(metrics) && hasAppGroupActionState && (
            <tbody>{getBodyContent(metrics, siteId)}</tbody>
          )}
          {!hasAppGroupActionState && (
            <>
              <div className="tooltip-content-no-recom-text">
                {tCommon('allSitesView.noRecom')}
              </div>
            </>
          )}
        </table>
      );
    } else if (data?.node?.sitesView === "availability") {
      const { siteId, name, city, usage, availability } = data.node;
      const iconWithColorStyle = {
        backgroundColor: getColor(
          formatNumberNDecimalPlaces(availability, 1),
          "availability"
        )
      };
      const siteNameDisplay = formatString(name,21);
      toolTip = (
        <div className="common-map-sites-tooltip-single-site">
          <div className="site-details">
            <div className="site-details-qoe">
              <div className="site-details-qoe-label hbr-type-body3">
                {tCommon('siteHoverId')}
              </div>
              <div className="site-details-qoe-value">
                <div className="hbr-type-body3">
                  <a className="sitename" data-cy={siteId} onClick={(e) => getSiteDetails(e, siteId)}>
                    {siteNameDisplay}
                  </a>
                </div>
              </div>
            </div>
            <div className="site-details-usage">
              <div className="site-details-usage-label hbr-type-body3">
                {tCommon('geoMapLocation')}
              </div>
              <div className="hbr-type-body3">{city}</div>
            </div>
            <div className="site-details-qoe">
              <div className="site-details-qoe-label hbr-type-body3">
                {tCommon('geoMapAvailability')}
              </div>
              <div className="site-details-qoe-value">
                <div
                  className="site-details-qoe-value-icon"
                  style={iconWithColorStyle}
                >
                  &nbsp;
                </div>
                <div className="hbr-type-body3">
                  {formatNumberNDecimalPlaces(availability, 1)}
                </div>
              </div>
            </div>
            <div className="site-details-usage">
              <div className="site-details-usage-label hbr-type-body3">
                {tCommon('geoMapUsage')}
              </div>
              <div className="hbr-type-body3">
                {formatBytes(usage)}
              </div>
            </div>
          </div>
        </div>
      );
    }
    if(toolTip !== undefined && mount.mounted) {
      return renderReactNode(toolTip, el, true);
    }
  };



  const onClusterClicked = siteId => {
    const path = location.pathname.split("/")[
      location.pathname.split("/").length - 1
    ];

    if (selectedSite === -1 && isRecommendationPage(path)) {
      let path;
      if (location.pathname.includes("Recommendations")) {
        path = `${BASE_URL}/Dashboard/Recommendations`;
      } else {
        path = routeURLs.appInfoAtSite;
      }
      props.setSelectedSite(siteId);
      props.setAltoProps({ appGroup: appGroup });
      navigate(path);
    }
  };

  const getToolTipSettings = () => {
    return {
      showTooltipOnClusterClick: false,
      tooltipOnHover: true
    };
  };

  const getLegend = () => {
    const content = document.createElement("div");
    const cnt = <div>
      <div className='legends-details-container'>
        <div className='legendsvalue high'>&nbsp;</div>
        <div className='hbr-type-body3'>{tCommon('sitesHigh')}</div>
      </div>
      <div className='legends-details-container'>
        <div className='legendsvalue fair'>&nbsp;</div>
        <div className='hbr-type-body3'>{tCommon('sitesFair')}</div>
      </div>
      <div className='legends-details-container'>
        <div className='legendsvalue low'>&nbsp;</div>
        <div className='hbr-type-body3'>{tCommon('sitesLow')}</div>
      </div>
      <div className='legends-details-container'>
        <div className='legendsvalue unknown'>&nbsp;</div>
        <div className='hbr-type-body3'>{tCommon('sitesUnknown')}</div>
      </div>
    </div>;
    if (mount.mounted) {
      return renderReactNode(cnt, content);
    }

    return content;
  };

    // Listen for theme changes and update currentThemeBool
  useEffect(() => {
    const observer = createThemeChangeObserver(() => {
      setIsDarkTheme(getCurrentTheme() === 'dark');
    });
    return () => observer.disconnect();
  }, []);

  const createMapViual = () => {
    if (!mapRef.current) return;
    setLoading(true);
    let defaultInitProperties = {
      domHolder: mapRef.current,
      preserveDrawingBuffer: true,
      trackResize: true,
      darkMode: isDarkTheme,
      enableNav: showNavigation !== undefined ? showNavigation : true,
      enableLegend: showLegend !== undefined ? showLegend : true,
      points: isAvailabilityScore ? pointsToShowWithAvailability() : pointsToShowWithQoe(),
      /** Map background is blue color,temporary code commented */
      // stylesUrl: "mapbox://styles/mapbox/streets-v12", // style URL
      disableClustering: options?.disableClustering ? options?.disableClustering : false,
      mapSettings: options?.mapSettings ? options?.mapSettings : getMapSettings(),
      tooltipSettings: options?.tooltipSettings ? options?.tooltipSettings : getToolTipSettings(),
      getLegendContent: getLegend,
      getTooltipContent: getTooltipContent
    };

    if (!disableClustering) {
      defaultInitProperties = {
        ...defaultInitProperties,
        clusterProperties: getClusterProperties(),
        clusterMarkerProps: getClusterMarkerProps(),
        onClusterClick: onClusterClicked,
        getClusterMarker: e => getDonutChartCluster(e, getClusterMarkerProps())
      };
    }

    mapVisualRef.current = new GeoMap(defaultInitProperties);
    mapVisualRef.current?.init().then(() => {
      mapVisualRef.current?.map?.resize();
      if (isEntireMapShown) mapVisualRef.current?.fitBounds(100);
      mapVisualRef.current?.map?.triggerRepaint();

      // Set loading to false when the map is fully rendered and idle
      mapVisualRef.current?.map?.on('idle', () => {
        setLoading(false);
      });
    });
  };

  useEffect(() => {
    if (sites && sites.length > 0 && mapRef.current) {
      createMapViual();
    }
    return () => { // Function will be called on component unmount or map data changes
      if (mapVisualRef.current) {
        mapVisualRef.current.destroy?.(); // Clean up map instance if it has a destroy method
        mapVisualRef.current = null;
      }
    }
  }, [sites, sitesView, currentTimeStamp, isDarkTheme]);

  if (sites.length === 0)
    return (<NoDataAvailable text={tCommon('noSitesFoundMessage')} />)
  else
    return (
      <div
        className={css["geo-map"]}
        ref={mapRef}
        name="map-visual"
        id={!loading ? "isLoaded_map" : ""}
      />
    );
};

GeoMapComponent.propTypes = {
  sites: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  options: PropTypes.object.isRequired,
  childCount: PropTypes.object,
  children: PropTypes.object,
  node: PropTypes.object,
  setSelectedSite: PropTypes.func.isRequired,
  globalFilter: PropTypes.object.isRequired,
  sitesView: PropTypes.string.isRequired,
  setAltoProps: PropTypes.func.isRequired,
  actions: PropTypes.object,
  applications: PropTypes.object,
  props: PropTypes.object,
  isAvailabilityScore: PropTypes.bool
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHOC(GeoMapComponent))
);
